import { useCallback } from 'react'

/* ═══════════════════════════════════════════════════════════
   ChildRecipesTab — Desert Flora V2
   ═══════════════════════════════════════════════════════════ */
const ChildRecipesTab = ({ childRecipes, setChildRecipes, services, inputSlots, onConfirm }) => {

  /* ── helpers ── */
  const update = useCallback((i, field, val) => {
    setChildRecipes(childRecipes.map((cr, j) => j === i ? { ...cr, [field]: val } : cr))
  }, [childRecipes, setChildRecipes])

  const remove = useCallback((i) => {
    const cr = childRecipes[i]
    onConfirm({
      title: 'Remove Child Recipe',
      subtitle: 'This action cannot be undone',
      message: `Remove "${cr.label || cr.childServiceId || 'this child recipe'}"?`,
      actionLabel: 'Remove',
      onConfirm: () => setChildRecipes(childRecipes.filter((_, j) => j !== i))
    })
  }, [childRecipes, setChildRecipes, onConfirm])

  const add = useCallback(() => {
    setChildRecipes([
      ...childRecipes,
      { childServiceId: '', label: '', inputMapping: [], inclusionMode: 'always', sortOrder: childRecipes.length }
    ])
  }, [childRecipes, setChildRecipes])

  const updateMapping = useCallback((crIdx, mapIdx, field, val) => {
    const updated = childRecipes.map((cr, j) => {
      if (j !== crIdx) return cr
      return {
        ...cr,
        inputMapping: (cr.inputMapping || []).map((m, k) => k === mapIdx ? { ...m, [field]: val } : m)
      }
    })
    setChildRecipes(updated)
  }, [childRecipes, setChildRecipes])

  const addMapping = useCallback((crIdx) => {
    const updated = childRecipes.map((cr, j) => {
      if (j !== crIdx) return cr
      return {
        ...cr,
        inputMapping: [...(cr.inputMapping || []), { parentInputKey: '', childInputKey: '', multiplier: 1 }]
      }
    })
    setChildRecipes(updated)
  }, [childRecipes, setChildRecipes])

  const removeMapping = useCallback((crIdx, mapIdx) => {
    const updated = childRecipes.map((cr, j) => {
      if (j !== crIdx) return cr
      return {
        ...cr,
        inputMapping: (cr.inputMapping || []).filter((_, k) => k !== mapIdx)
      }
    })
    setChildRecipes(updated)
  }, [childRecipes, setChildRecipes])

  /* ── render ── */
  return (
    <div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <div className="section-title">Child Recipes ({childRecipes.length})</div>
          <div className="section-subtitle">Compose this service from sub-recipes with input mappings</div>
        </div>
        <button className="btn-ghost" onClick={add}>
          + Add Child Recipe
        </button>
      </div>

      {/* Empty state */}
      {childRecipes.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">{'\uD83E\uDDE9'}</div>
          <div className="empty-title">No child recipes defined</div>
          <div className="empty-subtitle">Add a child recipe to compose this service from sub-recipes</div>
        </div>
      )}

      {/* Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {childRecipes.map((cr, i) => (
          <div key={i} className="card">

            {/* Card top row: fields + delete */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>

              {/* Child Service */}
              <div style={{ flex: 2, minWidth: 180 }}>
                <div className="section-label" style={{ marginBottom: 4 }}>Child Service</div>
                <select
                  className="bare"
                  value={cr.childServiceId || ''}
                  onChange={e => update(i, 'childServiceId', e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">— select service —</option>
                  {(services || []).map(s => (
                    <option key={s._id || s.serviceId} value={s.serviceId}>
                      {s.name} ({s.serviceId})
                    </option>
                  ))}
                </select>
              </div>

              {/* Label Override */}
              <div style={{ flex: 1, minWidth: 120 }}>
                <div className="section-label" style={{ marginBottom: 4 }}>Label Override</div>
                <input
                  type="text"
                  className="bare"
                  value={cr.label || ''}
                  placeholder="Optional"
                  onChange={e => update(i, 'label', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              {/* Inclusion Mode */}
              <div style={{ minWidth: 130 }}>
                <div className="section-label" style={{ marginBottom: 4 }}>Inclusion Mode</div>
                <select
                  className="bare"
                  value={cr.inclusionMode || 'always'}
                  onChange={e => update(i, 'inclusionMode', e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="always">Always</option>
                  <option value="variation">By Variation</option>
                </select>
              </div>

              {/* Delete */}
              <button
                className="row-delete"
                onClick={() => remove(i)}
                title="Remove child recipe"
                style={{ marginBottom: 2 }}
              >
                {'\u2715'}
              </button>
            </div>

            {/* Input Mappings sub-section */}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)' }}>

              {/* Mappings header */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <span className="section-label" style={{ flex: 1 }}>Input Mappings</span>
                <button
                  className="btn-ghost"
                  onClick={() => addMapping(i)}
                  style={{ fontSize: 11, padding: '2px 10px' }}
                >
                  + map
                </button>
              </div>

              {/* Mapping rows */}
              {(cr.inputMapping || []).length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic', paddingBottom: 2 }}>
                  No input mappings
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(cr.inputMapping || []).map((m, mi) => (
                  <div key={mi} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                    {/* Parent input select */}
                    <select
                      className="bare"
                      value={m.parentInputKey || ''}
                      onChange={e => updateMapping(i, mi, 'parentInputKey', e.target.value)}
                      style={{ flex: 1, minWidth: 120 }}
                    >
                      <option value="">— parent input —</option>
                      {(inputSlots || []).map(s => (
                        <option key={s.key} value={s.key}>
                          {s.label || s.key}
                        </option>
                      ))}
                    </select>

                    {/* Arrow */}
                    <span style={{ color: 'var(--ink-4)', fontSize: 13, flexShrink: 0 }}>
                      {'\u2192'}
                    </span>

                    {/* Child input key */}
                    <input
                      type="text"
                      className="bare mono"
                      value={m.childInputKey || ''}
                      placeholder="child key"
                      onChange={e => updateMapping(i, mi, 'childInputKey', e.target.value)}
                      style={{ flex: 1, minWidth: 100 }}
                    />

                    {/* Multiplier symbol */}
                    <span style={{ color: 'var(--ink-4)', fontSize: 13, flexShrink: 0 }}>
                      {'\u00D7'}
                    </span>

                    {/* Multiplier value */}
                    <input
                      type="number"
                      className="bare mono"
                      value={m.multiplier ?? 1}
                      step={0.1}
                      onChange={e => updateMapping(i, mi, 'multiplier', parseFloat(e.target.value) || 1)}
                      style={{ width: 64 }}
                    />

                    {/* Delete mapping (no confirmation) */}
                    <button
                      className="row-delete"
                      onClick={() => removeMapping(i, mi)}
                      title="Remove mapping"
                    >
                      {'\u2715'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

          </div>
        ))}
      </div>
    </div>
  )
}

export default ChildRecipesTab
